import React, { useState, useEffect } from "react";
import LoadingSpinner from "../../components/LoadingSpinner";
import ErrorMessage from "../../components/ErrorMessage";
import { useLanguage } from "../../contexts/LanguageContext";
import type { AnalyticsFilters } from "../../types/analytics";
import { fetchAnalyticsFunnel } from "./analyticsApi";
import type { AnalyticsFunnelResponse } from "../../types/analytics";

interface FunnelTabProps {
  filters: AnalyticsFilters;
}

const FunnelTab: React.FC<FunnelTabProps> = ({ filters }) => {
  const { t } = useLanguage();
  const [data, setData] = useState<AnalyticsFunnelResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchAnalyticsFunnel(filters)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || t("analytics.error"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [filters.from, filters.to, filters.task, filters.operatorId, filters.outcome, t]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <LoadingSpinner />
      </div>
    );
  }
  if (error) {
    return <ErrorMessage message={error} onRetry={() => setError(null)} />;
  }
  if (!data) {
    return <p className="text-gray-500 text-center py-8">{t("analytics.noData")}</p>;
  }

  const { steps, dropoffByOutcome, outcomeCounts } = data;
  const stepKeys: Array<keyof typeof steps> = ["created", "sentToCC", "attempted", "reached", "orderCreated", "completed"];
  const stepLabels: Record<string, string> = {
    created: t("analytics.funnelCreated"),
    sentToCC: t("analytics.funnelSentToCC"),
    attempted: t("analytics.funnelAttempted"),
    reached: t("analytics.funnelReached"),
    orderCreated: t("analytics.funnelOrderCreated"),
    completed: t("analytics.funnelCompleted"),
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {stepKeys.map((key) => (
                  <th key={key} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    {stepLabels[key]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              <tr>
                {stepKeys.map((key) => (
                  <td key={key} className="px-4 py-3 text-sm font-semibold text-gray-900 whitespace-nowrap">
                    {steps[key] ?? 0}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {Object.keys(dropoffByOutcome || {}).length > 0 && (
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">{t("analytics.qualityProblematicBreakdown")} (not reached)</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Outcome</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Count</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Object.entries(dropoffByOutcome || {}).map(([outcome, count]) => (
                  <tr key={outcome}>
                    <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">{outcome || "(empty)"}</td>
                    <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">{count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default FunnelTab;
